'use strict';

interface NowOptions {
  height: number;
  width: number;
  nodes: any;
  bubble: any;
  force: any;
  foci: any;
  events?: Array<string>;
}

const standardForce: any = {
  gravity: .17,
  friction: .5,
  charge_diminisher: {
    main: .15,
    secondary: .2
  }
};

const defaultBubbleOptions = {
  main: {
    min_radius: 50,
    max_radius: 100,
    min_font_size: 20,
    max_font_size: 50,
    min_opacity: .6,
    max_opacity: 1
  },
  secondary: {
    min_radius: 40,
    max_radius: 80,
    min_font_size: 12,
    max_font_size: 30,
    min_opacity: .6,
    max_opacity: 1
  }
};

export default class OptionsProvider {

  private readonly options: NowOptions;

  constructor(options: NowOptions, element: HTMLElement) {
    this.options = Object.assign({}, OptionsProvider.defaultOptions(element), options);
  }

  /**
   * Returns all mobile options.
   * @param {object} element - The container of the visualisation as HTML element.
   * @returns {object} The mobile option.
   */
  private static defaultOptions(element: HTMLElement): any {
    const height = element ? element.clientHeight : 768;
    const width = element ? element.clientWidth : 1024;
    return {
      nodes: {
        fetch: 50,
        main: width / 40,
        secondary: width / 80
      },
      height: height,
      width: width,
      force: standardForce,
      bubble: this.getBubbleOptions(width),
      foci: this.getFoci(width, height)
    };
  }

  /**
   * Returns bubble config object based on element width.
   * @param {number} width - The with of the HTML element.
   * @returns {object} The bubble option.
   */
  private static getBubbleOptions(width: number): any {
    const bubbleOption = Object.assign({}, defaultBubbleOptions);

    if (width >= 1024) {
      bubbleOption.main.min_radius = width / 39;
      bubbleOption.main.max_radius = width / 18;
      bubbleOption.main.min_font_size = 15;
      bubbleOption.main.max_font_size = 25;
      bubbleOption.secondary.min_radius = width / 48;
      bubbleOption.secondary.max_radius = width / 26;
      bubbleOption.secondary.min_font_size = 15;
      bubbleOption.secondary.max_font_size = 25;
    } else if (width > 425) {
      bubbleOption.main.min_radius = width / 20;
      bubbleOption.main.max_radius = width / 16;
      bubbleOption.main.min_font_size = 20;
      bubbleOption.main.max_font_size = 30;
      bubbleOption.secondary.min_radius = width / 28;
      bubbleOption.secondary.max_radius = width / 24;
      bubbleOption.secondary.min_font_size = 20;
      bubbleOption.secondary.max_font_size = 30;
    } else {
      bubbleOption.main.min_radius = width / 9;
      bubbleOption.main.max_radius = width / 4;
      bubbleOption.main.min_font_size = 20;
      bubbleOption.main.max_font_size = 40;
      bubbleOption.secondary.min_radius = width / 11;
      bubbleOption.secondary.max_radius = width / 5;
      bubbleOption.secondary.min_font_size = 20;
      bubbleOption.secondary.max_font_size = 40;
    }
    return bubbleOption;
  }

  /**
   * Returns focus config object based on element width and height.
   * @param {number} width - The with of the HTML element.
   * @param {number} height - The with of the HTML element.
   * @returns {object} The focus option.
   */
  private static getFoci(width: number, height: number): any {
    return {
      single: [
        { 'x': width / 2, 'y': height / 2 }
      ],
      double: [
        { 'x': width * 7 / 20, 'y': height / 2 },
        { 'x': width * 16 / 20, 'y': height / 2 }
      ],
      triple: [
        { 'x': width * 7 / 20, 'y': height / 2 },
        { 'x': width * 17 / 20, 'y': height * 0.3 },
        { 'x': width * 15 / 20, 'y': height * 3 / 4 }
      ]
    };
  }

  /**
   * Returns the value of the specified option, or the full options object if name is not specified.
   * @param {string} name (Optional) - The name of the option.
   * @returns {object} The requested option.
   */
  public get(name: string = null): any {
    let option = this.options;
    if (name) {
      name.split('.').forEach((attr) => {
        option = option[attr];
      });
    }
    return option;
  }
}

export {
  NowOptions
};
